import React from 'react'
import { durationBetweenDates } from 'utils/misc'
import { humanReadableSize } from 'openstack/helpers'
import { GPUFlavorTagCellComponent } from 'k8s/components/cluster/deployment/form-components/GPUTag'
import { routeManager } from 'core/utils/routes'
import SimpleLink from 'core/components/SimpleLink'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import moment from 'moment'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { VolumeSourceCell } from '../storage/volumes/volume-details/VolumeSourceCell'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import ImageStatusInfo from '../images/ImageStatusInfo'
import { TableHeaderTooltip, TPMModelText } from '../images/ImagesListPage'
import { keys } from 'ramda'

interface ExtraDriveInfo {
  volumes: any[]
  hasExtraDrives: boolean
  count: number
}

export const hasOtherUserVm = ({ vms, userId }) => {
  return vms?.some((vm) => vm?.user_id !== userId)
}

export const visibleImageColumns = [
  'name',
  'imageTypeDisplay',
  'disk_format',
  'virtual_size',
  'properties',
  'created_at',
]

export const imageColumnsOrder = [
  'name',
  'id',
  'status',
  'imageTypeDisplay',
  'visibility',
  'protected',
  'disk_format',
  'virtual_size',
  'size',
  'hw_tpm_model',
  'tags',
  'properties',
  'created_at',
]

export const getVmSnapshotColumns = ({ vmsById, volumeSnapshotsById }) => [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'status',
    label: (
      <TitleWithInfoIcon
        titleText="Status"
        tooltipMsg={<ImageStatusInfo entityType={'VM snapshot'} />}
      />
    ),
    display: false,
  },
  {
    id: 'imageTypeDisplay',
    label: 'Type',
  },
  {
    id: 'instance_uuid',
    label: 'Source VM',
    display: false,
    render: (val) =>
      val ? (
        <SimpleLink
          src={routeManager.routes.openstack.vmDetails.path({
            id: val,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          {vmsById?.(val)?.name || val}
        </SimpleLink>
      ) : null,
  },
  {
    id: 'snapshotIds',
    label: 'Volume Snapshots',
    display: false,
    render: (val) =>
      val.length ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          {val?.map((snapshotId) => (
            <div key={snapshotId}>
              <SimpleLink
                src={routeManager.routes.openstack.volumeSnapshotDetails.path({
                  id: snapshotId,
                  tab: IVolumeSnapshotDetailsPageTabs.Overview,
                })}
              >
                {volumeSnapshotsById?.[snapshotId]?.name || snapshotId}
              </SimpleLink>
            </div>
          ))}
        </div>
      ) : null,
  },
  {
    id: 'visibility',
    label: 'Visibility',
    display: false,
  },
  {
    id: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'disk_format',
    label: 'Disk Format',
  },
  {
    id: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the VM disk that will get created from this snapshot" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    id: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the snapshot file that is stored in the Image Library" />
        }
      />
    ),
    render: humanReadableSize,
    display: false,
  },
  {
    id: 'owner_user_name',
    label: 'Snapshot Owner',
    display: false,
  },
  {
    id: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
    display: false,
  },
  {
    id: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
  },
]

export const visibleVmSnapshotColumns = [
  'name',
  'imageTypeDisplay',
  'instance_uuid',
  'snapshotIds',
  'disk_format',
  'virtual_size',
  'created_at',
]

export const vmSnapshotColumnsOrder = [
  'name',
  'id',
  'status',
  'imageTypeDisplay',
  'instance_uuid',
  'snapshotIds',
  'visibility',
  'protected',
  'disk_format',
  'virtual_size',
  'size',
  'owner_user_name',
  'hw_tpm_model',
  'tags',
  'created_at',
]

export const volumeColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'hostName',
    label: 'Host',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'backend',
    label: 'Volume Backend',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'tenantName',
    label: 'Tenant',
    display: false,
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'volume_type',
    label: 'Type',
  },

  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'device',
    label: 'Device',
    display: false,
  },
  {
    id: 'snapshots',
    label: 'Snapshots',
    render: (snapshots) => snapshots?.length,
    display: false,
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
  },
  {
    id: 'multiattach',
    label: 'Multiattach',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'source',
    label: (
      <TitleWithInfoIcon
        titleText="Source"
        tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
      />
    ),
    render: (val, item) => <VolumeSourceCell item={item} />,
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d'] })(value),
  },
  {
    id: 'metadata',
    label: 'Metadata',
    render: (value) => createResourceLabelsCell({ type: 'table', separator: '=' }),
    display: false,
  },
]

const getGpuInfo = (metadata) => {
  // should only be one or the other
  const passthrough = metadata?.['pci_passthrough:alias']
  const vgpu = metadata?.['resources:VGPU']
  if (passthrough) {
    const [profile, count] = passthrough.split(':')
    return {
      type: 'Passthrough',
      count,
      profile,
    }
  }
  if (vgpu) {
    return {
      type: 'vGPU',
      slices: vgpu,
    }
  }
  return {}
}

export const flavorColumns = [
  {
    id: 'name',
    label: 'Name',
    render: (value, item) => GPUFlavorTagCellComponent({ value, item }),
  },
  {
    id: 'vcpus',
    label: 'vCPUs',
  },
  {
    id: 'ram',
    label: 'RAM',
    render: (ram) => humanReadableSize(ram * 1024 * 1024),
  },
  {
    id: 'disk',
    label: 'Disk',
    render: (disk) => humanReadableSize(disk * 1024 * 1024 * 1024),
  },
]

export const flavorColumnsGpu = [
  ...flavorColumns,
  {
    id: 'gpuFlavor',
    label: 'GPU Support',
    render: (value, item) => {
      const metadata = item?.extra_specs
      const gpuInfo = getGpuInfo(metadata)
      const { type, count, slices } = gpuInfo

      if (!type) return null

      return (
        <div>
          <div>{type}</div>
          {type === 'Passthrough' && count && <div>GPU Count: {count}</div>}
          {type === 'vGPU' && slices && <div>Slices: {slices}</div>}
        </div>
      )
    },
  },
  {
    id: 'extra_specs',
    label: 'Profile',
    render: (value, item) => {
      const metadata = item?.extra_specs
      const { profile } = getGpuInfo(metadata)
      return <div>{profile}</div>
    },
  },
]

export const visibleFlavorColumns = ['name', 'vcpus', 'ram', 'disk']

export const visibleGpuFlavorColumns = ['name', 'vcpus', 'ram', 'disk', 'gpuFlavor', 'extra_specs']

export const securityGroupColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

export const volumeSnapshotColumns = [
  {
    id: 'name',
    label: 'name',
  },
  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
]

export const convertToBytes = (value: string | number, unit: 'GB' | 'MB' | 'KB' | 'B') => {
  const val = Number(value)
  switch (unit) {
    case 'GB':
      return val * 1024 * 1024 * 1024
    case 'MB':
      return val * 1024 * 1024
    case 'KB':
      return val * 1024
    case 'B':
      return val
  }
}

export const isFlavorCompatibleWithImageAndVolume = (flavor, params) => {
  const imageSize = params.image?.[0]?.virtual_size
  const volumeSize = convertToBytes(params?.volumeSize, 'GB') || 0
  const flavorSize = flavor?.disk >= 0 ? convertToBytes(flavor?.disk, 'GB') || 0 : 0

  // Flavor must be larger than the image size
  const isCompatibleFlavor =
    flavorSize === 0 || params?.sourceType === 'newVolume' || !imageSize || flavorSize >= imageSize
  return !isCompatibleFlavor
}
export const isImageCompatibleWithVolumeAndFlavor = (image, params) => {
  const imageSize = image?.virtual_size
  const volumeSize = convertToBytes(params?.volumeSize, 'GB')
  const flavorSize = convertToBytes(params?.flavor[0]?.disk, 'GB') || null

  // Volume must be larger than the image size
  const imageValid = flavorSize === null ? true : flavorSize >= 0 ? flavorSize >= imageSize : true
  const disabled = params?.sourceType === 'newVolume' ? imageSize > volumeSize : !imageValid
  return disabled
}

export const filterVmsByRole = (userRole, userId, allTenants, allOwners, vms, allVms) => {
  switch (userRole) {
    case 'admin':
      return allTenants ? allVms : vms
    case 'member':
      if (!allOwners) {
        return vms?.filter((vm) => vm?.user_id === userId)
      }
      return vms
    case 'reader':
      return vms
    default:
      return vms
  }
}

export const pageSizeOptions = [3, 5, 10].map((value) => ({ value }))

export const formatTimestampToLocal = (time: string): string => {
  if (!time) return '-' // Handle empty or undefined input
  return moment
    .utc(time)
    .local() // Convert the UTC time to the browser/system's local timezone.
    .format('LL LTS')
}

export const closeOnEscape = (onClose: () => void) => {
  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  window.addEventListener('keydown', handleEscapeKeyPress)
  return () => {
    window.removeEventListener('keydown', handleEscapeKeyPress)
  }
}

// Has a volume with boot index = 0
export const isVolumeBackedVm = (vm) => {
  const volumeKeys = keys(vm?.volumeDetails)
  return volumeKeys?.some((key) => {
    const volume = vm?.volumeDetails?.[key]
    return volume?.boot_index === 0
  })
}

export const canSoftReboot = (vm) => {
  return vm?.state === 'active'
}

export const canHardReboot = (vm) => {
  const validStates = ['active', 'error', 'hard_reboot', 'paused', 'reboot', 'shutoff', 'suspended']
  return validStates.includes(vm?.state)
}

export const getExtraDriveVolumesInfo = (vm: any): ExtraDriveInfo => {
  // Filter volumes that represent "extra" drives (not the primary OS disk)
  const volumes = Object.values(vm?.volumeDetails || {}).filter((vol: any) => {
    // Condition 1: Was it hot-plugged later with our specific API tag?
    const hasExtraDriveTag = vol.tag === 'extra_drive'

    // Condition 2: Was it attached during VM creation as a virtual CD-ROM?
    const isCdrom = vol.device_type === 'cdrom'

    // Ensure we never accidentally unmount the primary operating system drive
    const isNotPrimaryBoot = vol.boot_index !== 0

    // An extra drive is EITHER explicitly tagged OR it is a non-primary CD-ROM
    return hasExtraDriveTag || (isCdrom && isNotPrimaryBoot)
  })

  // Return both the array and derived properties to avoid recomputation
  return {
    volumes, // The actual extra drive volume objects
    hasExtraDrives: volumes.length > 0, // Quick boolean check
    count: volumes.length, // How many extra drives are attached
  }
}
